import React, { Children, forwardRef, useMemo } from 'react'
import PropTypes from 'prop-types'
import Skeleton from '../Skeletons/Skeleton'
import BaseTableRow from './TableRow'
import BaseTableCell from './TableCell'
import { BaseTable, BaseTableBody, BaseTableHead, Root } from './Table.styles'

export const TableCell = BaseTableCell
export const TableRow = BaseTableRow

/**
 * The Table's component.
 */
const Table = forwardRef((props, ref) => {
  const {
    accessibility,
    borderSpacing,
    children = null,
    height,
    disableLatestBottomBorder,
    enableCondensed,
    enableStripped,
    loading,
    margin,
    padding,
    totalLoadingBars,
    ...restProps
  } = props
  const [headChild, bodyChildren] = useMemo(() => {
    const mappedChildren = Children.toArray(children)
    const headChild = mappedChildren.find(child => child.props.enableHead)
    const bodyChildren = mappedChildren.filter(child => !child.props.enableHead)

    return [headChild, bodyChildren]
  }, [children])
  const skeleton = useMemo(() => {
    const count = headChild ? Children.count(headChild?.props?.children) : 1

    return (
      <BaseTableBody>
        <TableRow>
          {Array(count)
            .fill(0)
            .map((rowKey, rowIndex) => (
              <TableCell key={`table-row-${rowKey}-${rowIndex}`}>
                {Array(totalLoadingBars)
                  .fill(0)
                  .map((cellKey, cellIndex) => (
                    <Skeleton
                      height="35px"
                      key={`table-cell-${rowKey}-${rowIndex}-${cellKey}-${cellIndex}`}
                      margin="0 0 0.4rem"
                      variant="rectangular"
                      width="100%"
                    />
                  ))}
              </TableCell>
            ))}
        </TableRow>
      </BaseTableBody>
    )
  }, [headChild, totalLoadingBars])

  return (
    <Root height={height} margin={margin} padding={padding} ref={ref}>
      <BaseTable {...restProps} aria-label={accessibility.label} borderSpacing={borderSpacing} tabIndex="0">
        {headChild && <BaseTableHead {...restProps}>{headChild}</BaseTableHead>}
        {loading && skeleton}
        {!loading && (
          <BaseTableBody
            {...restProps}
            disableLatestBottomBorder={disableLatestBottomBorder}
            enableCondensed={enableCondensed}
            enableStripped={enableStripped}
          >
            {bodyChildren}
          </BaseTableBody>
        )}
      </BaseTable>
    </Root>
  )
})

Table.defaultProps = {
  accessibility: { label: 'table' },
  borderSpacing: '0',
  disableLatestBottomBorder: false,
  enableCondensed: false,
  enableStripped: false,
  height: 'auto',
  loading: false,
  margin: '0',
  padding: 'initial',
  totalLoadingBars: 6
}

Table.propTypes = {
  /**
   * Accessibility.
   */
  accessibility: PropTypes.shape({
    label: PropTypes.string
  }),
  /**
   * Border spacing.
   */
  borderSpacing: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  /**
   * Children.
   */
  children: PropTypes.node,
  /**
   * Disable latest bottom border
   */
  disableLatestBottomBorder: PropTypes.bool,
  /**
   * Enable condensed.
   */
  enableCondensed: PropTypes.bool,
  /**
   * Enable stripped.
   */
  enableStripped: PropTypes.bool,
  /**
   * Height.
   */
  height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  /**
   * Loading.
   */
  loading: PropTypes.bool,
  /**
   * Margin.
   */
  margin: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  /**
   * Padding.
   */
  padding: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  /**
   * Total loading bars.
   */
  totalLoadingBars: PropTypes.number
}

export default Table
